import classNames from "classnames";
import React, { FunctionComponent, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ActionType,
  DrugCostInfo,
  PharmacyInfo,
  PlanDetailsPageSection,
} from "../../../@types";
import { Routes } from "../../../app";
import { CheckmarkIcon } from "../../../components";
import { sendTealiumEvent, useTranslate } from "../../../helpers";
import { createGa4Event, Ga4Event } from "../../../helpers/analytics";
import { toTitleCase } from "../../../helpers/formatHelpers";
import { useGetPharmacies } from "../DrugCoverageSection/drugCoverageHelpers";
import { Location } from "history";
import { Badge } from "@cmsgov/ds-medicare-gov";
import { useFlags } from "launchdarkly-react-client-sdk";
import { AppStore } from "../../../app/store";
import { v1 as uuidv1 } from "uuid";

interface BasePharmacyNetworkPropsInterface
  extends React.ComponentPropsWithoutRef<"div"> {
  // @see https://github.cms.gov/pages/MedicareGov/mgov-design-system/components/table
  // Add className(s) to this component when displayed in a table in "stacked",
  // responsive mode (e.g., a utility class for adding margin on the bottom)
  includeName?: boolean;
  location?: Location;
  planLongId?: string;
  stackedViewClassName?: string;
  stackedViewScreenReaderContent?: string;
  hasDrugCoverage?: boolean;
  showCostsVaryForMO?: boolean;
  mailOrderNotCovered?: boolean;
}

// Set up two interfaces that are mutually exclusive. In one, drugCostInfo is
// required and pharmacyInfo is not allowed. The other is the converse.
interface PharmacyNetworkStatusFromStateProps
  extends BasePharmacyNetworkPropsInterface {
  drugCostInfo: DrugCostInfo;
  npi: string;
  pharmacy?: never;
}

interface PharmacyNetworkStatusProps extends BasePharmacyNetworkPropsInterface {
  pharmacy?: PharmacyInfo;
  drugCostInfo?: never;
  npi?: never;
}

const BasePharmacyNetworkStatus: FunctionComponent<PharmacyNetworkStatusProps> = ({
  includeName = false,
  location,
  planLongId,
  stackedViewClassName = "",
  stackedViewScreenReaderContent = "",
  hasDrugCoverage = false,
  showCostsVaryForMO = false,
  pharmacy,
  mailOrderNotCovered = false,
  ...props
}) => {
  const t = useTranslate();
  const { dispatch, state } = useContext(AppStore.AppContext);
  const flags = useFlags();

  useEffect(() => {
    pharmacy &&
      dispatch({
        type: ActionType.UPDATE_PHARMACY_NETWORK_STATUS,
        payload: pharmacy,
      });
  }, []);

  const pharmacyTypeClasses = classNames(
    {
      "ds-u-margin-bottom--0": includeName,
      "ds-u-margin--0": !includeName,
    },
    "ds-u-lg-margin-top--1",
    "ds-u-display--flex",
    "ds-u-align-items--start"
  );

  const componentClasses = classNames(
    "PharmacyNetworkStatus",
    stackedViewClassName
  );

  let statusMessage = "";

  if (pharmacy?.in_network) {
    if (pharmacy?.preferred) {
      statusMessage = t("plan_details.pharmacies.preferred_in_network");
    } else {
      statusMessage = t("plan_details.pharmacies.standard_in_network");
    }
  } else {
    if (mailOrderNotCovered) {
      statusMessage = t("plan_terms.does_not_cover_mail_order");
    } else {
      statusMessage = t("plan_details.pharmacies.out_of_network_pharmacy");
    }
  }

  // For A11Y, add all values as `aria-describedby` to a sr-only element and aria-hide
  // the original elements. This way, all information is read together, and the discrete
  // elements are removed from the accessibility tree.
  // If more content is passed into the `stackedViewScreenReaderContent` prop, it
  // is appended to this `aria-describedby` block, with suitable pauses.
  const srContent = `${pharmacy?.name}, ${statusMessage}${
    stackedViewScreenReaderContent ? `, ${stackedViewScreenReaderContent}` : ""
  }${
    showCostsVaryForMO
      ? `, ${t("plan_details.pharmacies.costs_vary_for_mail_order")}`
      : ""
  }`;

  if (pharmacy) {
    const uniqueId = `${pharmacy?.npi}-${uuidv1()}`;
    return (
      <>
        <div id={uniqueId} className="ds-u-visibility--screen-reader">
          {srContent}
        </div>
        <div
          className={componentClasses}
          aria-describedby={uniqueId}
          aria-hidden="true"
          {...props}
        >
          {includeName && (
            <span className="ds-u-font-weight--bold">
              {toTitleCase(pharmacy.name)}
            </span>
          )}
          <div className={pharmacyTypeClasses}>
            {pharmacy.preferred ? (
              <Badge data-testid="preferredBadge" variation="success">
                {t("pharmacy.status.preferred")}
              </Badge>
            ) : (
              <CheckmarkIcon checked={pharmacy.in_network} />
            )}

            <span className="ds-u-margin-left--1">
              <span>
                {statusMessage}
                {!pharmacy.in_network &&
                  location &&
                  planLongId &&
                  hasDrugCoverage && (
                    <>
                      {" "}
                      <Link
                        className="mct-hide-print"
                        to={{
                          pathname: `${Routes.inNetworkPharmacy.replace(
                            ":id",
                            planLongId
                          )}`,
                          state: {
                            nextRoute: {
                              ...location,
                              hash: PlanDetailsPageSection.DRUG_COVERAGE,
                            },
                          },
                        }}
                        onClick={() => {
                          sendTealiumEvent(
                            "Find Plans - Add Pharmacy",
                            "View pharmacy search results"
                          );
                          createGa4Event({
                            event_name: Ga4Event.PHARMACY_SEARCH_RESULTS,
                            click_text: "Find an in-network pharmacy",
                            state,
                            flags,
                          });
                        }}
                      >
                        {t(
                          "plan_details.pharmacies.find_an_in_network_pharmacy"
                        )}
                      </Link>
                    </>
                  )}
              </span>
              {showCostsVaryForMO && (
                <div className="ds-u-margin-top--1 ds-u-font-size--sm">
                  {t("plan_details.pharmacies.costs_vary_for_mail_order")}
                </div>
              )}
            </span>
          </div>
        </div>
      </>
    );
  } else {
    return null;
  }
};

const PharmacyNetworkStatusFromState: FunctionComponent<PharmacyNetworkStatusFromStateProps> = ({
  drugCostInfo,
  npi,
  ...props
}) => {
  const pharmacy = useGetPharmacies(drugCostInfo).find(p => p.npi === npi);
  return <BasePharmacyNetworkStatus pharmacy={pharmacy} {...props} />;
};

// The union type here for props allows only one or the other, drugCostInfo + npi
// or pharmacyInfo, not both
const PharmacyNetworkStatus: FunctionComponent<
  PharmacyNetworkStatusProps | PharmacyNetworkStatusFromStateProps
> = props => {
  const { drugCostInfo, npi, pharmacy, ...rest } = props;
  // `npi` here may be an empty string, so explicitly check it's not undefined
  return drugCostInfo && typeof npi !== "undefined" ? (
    <PharmacyNetworkStatusFromState
      drugCostInfo={drugCostInfo}
      npi={npi}
      {...rest}
    />
  ) : (
    <BasePharmacyNetworkStatus pharmacy={pharmacy} {...rest} />
  );
};

const PharmacyNetworkStatusStackedHeading: FunctionComponent<
  PharmacyNetworkStatusProps | PharmacyNetworkStatusFromStateProps
> = ({
  stackedViewClassName = "ds-u-margin-bottom--2 ds-u-lg-margin-bottom--0",
  ...props
}) => (
  <PharmacyNetworkStatus
    stackedViewClassName={stackedViewClassName}
    {...props}
  />
);

export { PharmacyNetworkStatus, PharmacyNetworkStatusStackedHeading };
